import { ethers } from 'ethers'
import ABIS from './contractabis.js'

import { kulaApiFetch } from './kulaApiFetch.js'

/**
 * @param {string} selectedDao
 * @returns {Promise<Proposal[]>}
 */
export async function getAllProposals(selectedDao) {
  /** @type {Proposal[]} */
  let proposals = []
  try {
    proposals = await kulaApiFetch(
      `/v1/contracts/daos/proposals?daoaddress=${selectedDao}`,
      {
        method: 'GET',
      }
    ).then((response) => response.json())
  } catch (err) {
    throw new Error(err)
  }

  const sortedProposals = proposals?.sort((a, b) => +b.id - +a.id)

  const results = []
  for (const proposal of sortedProposals) {
    try {
      const proposalWithState = await getProposalState(proposal)
      results.push(proposalWithState)
    } catch (err) {
      if (
        err?.reason === 'Governor: unknown proposal id' ||
        (err?.errorArgs &&
          err.errorArgs[0] === 'Governor: unknown proposal id') ||
        (err?.message && err.message.includes('Governor: unknown proposal id'))
      ) {
        continue
      }
      throw err
    }
  }
  return results
}

/**
 * @param {Proposal} proposal
 * @returns {Promise<Proposal>}
 */
export async function getProposalState(proposal) {
  const proposalStatus = await getState(proposal.id)
  return {
    ...proposal,
    status: proposalStatus,
  }
}

/**
 * @param {string} proposalId
 * @returns {Promise<ProposalStatus>}
 */
export async function getState(proposalId) {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.address,
    abiMethods: ABIS.REGIONAL_DAO.methods,
  })

  // TODO: W3 READ - Uses Infura token via contract.state call
  return contract.state(proposalId)
}

/**
 * @param {DaoSettings} dao
 * @returns {string[]} Array of addresses
 */
export function getRegionalDaoOwners(dao) {
  try {
    const contract = _getContract({
      daoAddress: dao.multiSigAddress,
      abiMethods: ABIS.MULTI_SIG.methods,
    })

    // TODO: W3 READ - Uses Infura token via contract.getExecutives call
    return contract.getExecutives()
  } catch (error) {
    return []
  }
}

/**
 * @param {Object} params
 * @param {string} params.walletAddress
 * @param {DaoSettings} params.dao
 * @returns {Promise<boolean>}
 */
export async function isRegionalDaoExecutive({ walletAddress, dao }) {
  const executives = await getRegionalDaoOwners(dao)
  return executives.includes(walletAddress)
}

export async function userHasVotedOnProposal({
  daoAddress,
  proposalId,
  userWalletAddress,
}) {
  // TODO: W3 READ - Uses Infura token via contract.hasVoted call
  return _getContract({
    daoAddress,
    abiMethods: ABIS.REGIONAL_DAO.methods,
  }).hasVoted(proposalId, userWalletAddress)
}

/**
 * @param {{
 * daoAddress: string,
 * proposerWalletAddress: string,
 * registryAddress: string,
 * proposalDescription: string
 * registryAbiMethods?: string[]
 * }} param0
 * @returns {Promise<*>}
 */
export async function createPIPProposal({
  daoAddress,
  proposerWalletAddress,
  registryAddress,
  proposalDescription,
  registryAbiMethods = ABIS.REGISTRY.methods,
}) {
  const provider = new ethers.providers.Web3Provider(window.ethereum)
  const signer = provider.getSigner()
  const gasLimit = 3000000

  const registryContract = new ethers.Contract(
    registryAddress,
    registryAbiMethods,
    signer
  )
  const encodedFunction = registryContract.interface.encodeFunctionData(
    'storeDaoAction',
    [daoAddress, proposerWalletAddress, proposalDescription]
  )

  return await _getContract({
    daoAddress,
    abiMethods: ABIS.REGIONAL_DAO.methods,
  }).propose([registryAddress], [0], [encodedFunction], proposalDescription, {
    gasLimit,
  })
}

export async function voteOnProposal({ proposal, vote }) {
  const gasLimit = 3000000
  await _getContract({
    daoAddress: proposal.daoAddress,
    abiMethods: ABIS.REGIONAL_DAO.methods,
  }).castVote(proposal.id, vote, { gasLimit })
}

function _getContract({ daoAddress, abiMethods }) {
  // TODO: W3 READ - Uses Infura token via window.ethereum provider
  return new ethers.Contract(
    daoAddress,
    abiMethods,
    new ethers.providers.Web3Provider(window.ethereum).getSigner()
  )
}

// TODO: These functions are not implemented yet but will be used in the future
export function approvePendingProposal(proposal) {
  return Promise.resolve(null)
}
export function rejectPendingProposal(proposal) {
  return Promise.resolve(null)
}

/** @param {WalletAccount} account */
export async function getUserVotes(account) {
  let userVotes = []

  try {
    const proposals = await getAllProposals(window.Globals.selectedDao.address)

    await Promise.all(
      proposals.map(async (proposal) => {
        const votes = await getVote(proposal.id)

        votes?.map((vote) => {
          if (vote.voterAddress === account.walletAddress) {
            userVotes.push({
              ...vote,
              proposalId: proposal.id,
              // Create a unique identifier since we don't have transaction hash
              id: `${proposal.id}-${vote.voterAddress}`,
            })
          }
        })
      })
    )
  } catch (error) {
    console.error('Error in getUserVotes:', error)
    // Error getting user votes
  }

  return userVotes
}

//TODO: Update this function to return count of proposals NOT voted on.
/**
 * @param {DaoSettings} selectedDao
 * @param {ProposalStatus} status
 */
export async function getProposalCount(selectedDao, status) {
  return Promise.resolve(
    (await getAllProposals(selectedDao.address)).filter(
      (proposal) => proposal.status === status && !proposal.hidden
    ).length
  )
}

/**
 * @param {DaoSettings} selectedDao
 * @returns {Promise<number>}
 */
export async function getExecutiveProposalCount(selectedDao) {
  const proposals = await getAllProposals(selectedDao.address)
  return proposals.filter(
    (proposal) =>
      proposal.status === 4 || proposal.status === 5 || proposal.hidden === true
  ).length
}

/**
 * @param {Proposal['id']} proposalId
 */
export async function getVote(proposalId) {
  const response = await kulaApiFetch(
    `/v1/contracts/daos/votes?proposalid=${proposalId}`,
    {
      method: 'GET',
    }
  )

  const votes = await response.json()
  return votes
}

/**
 * @returns {Promise<number>}
 */
export async function getCurrentBlockNumber() {
  // TODO: W3 READ - Uses Infura token via window.ethereum provider
  const provider = new ethers.providers.Web3Provider(window.ethereum)
  // TODO: W3 READ - Uses Infura token via provider.getBlockNumber call
  const blockNumber = await provider.getBlockNumber()
  return blockNumber
}

/**
 * @param {number} numBlocks
 * @returns {Promise<number>}
 */
export async function getAverageBlockTime(numBlocks = 10) {
  // TODO: W3 READ - Uses Infura token via window.ethereum provider
  const provider = new ethers.providers.Web3Provider(window.ethereum)
  // TODO: W3 READ - Uses Infura token via provider.getBlockNumber call
  const latestBlockNumber = await provider.getBlockNumber()

  let blockTimes = []

  for (let i = latestBlockNumber - numBlocks + 1; i <= latestBlockNumber; i++) {
    // TODO: W3 READ - Uses Infura token via provider.getBlock call
    const block = await provider.getBlock(i)
    blockTimes.push(block.timestamp)
  }

  const timeDifferences = blockTimes
    .slice(1)
    .map((time, index) => time - blockTimes[index])
  const averageBlockTime =
    timeDifferences.reduce((acc, curr) => acc + curr, 0) /
    timeDifferences.length

  return averageBlockTime
}

/**
 * @param {{daoAddress: string, description: string,
 * proposerWalletAddress: string, registryAddress: string, proposalId: string}} proposal
 */
export async function queueTransaction({
  daoAddress,
  description,
  proposerWalletAddress,
  registryAddress,
  proposalId,
}) {
  // TODO: W3 READ - Uses Infura token via window.ethereum provider
  const governorContract = new ethers.Contract(
    daoAddress,
    ABIS.REGIONAL_DAO_GOVERNOR.methods,
    new ethers.providers.Web3Provider(window.ethereum).getSigner()
  )

  // TODO: W3 READ - Uses Infura token via contract.multiSigWallet call
  const multiSigFromGovernor = await governorContract.multiSigWallet()

  const descriptionHash = ethers.utils.keccak256(
    ethers.utils.toUtf8Bytes(description)
  )

  // TODO: W3 READ - Uses Infura token via window.ethereum provider
  const registryContract = new ethers.Contract(
    registryAddress,
    ABIS.REGISTRY.methods,
    new ethers.providers.Web3Provider(window.ethereum).getSigner()
  )

  const encodedFunction = registryContract.interface.encodeFunctionData(
    'storeDaoAction',
    [daoAddress, proposerWalletAddress, description]
  )

  const calldatas = [encodedFunction]
  const values = [0]
  const targets = [registryAddress]

  // TODO: W3 READ - Uses Infura token via contract.hashProposal call
  const calculatedProposalId = await governorContract.hashProposal(
    targets,
    values,
    calldatas,
    descriptionHash
  )

  if (calculatedProposalId.toString() !== proposalId.toString()) {
    console.error(
      'MISMATCH DETECTED! Using original parameters from blockchain...'
    )
    const originalParams = await getOriginalProposalParameters(
      daoAddress,
      proposalId
    )
    if (originalParams) {
      return queueWithOriginalParameters(
        governorContract,
        originalParams,
        proposalId
      )
    } else {
      throw new Error(
        'Could not find original proposal parameters from blockchain events'
      )
    }
  }

  try {
    const tx = await governorContract.queue(
      targets,
      values,
      calldatas,
      descriptionHash,
      {
        gasLimit: 5000000,
      }
    )

    const receipt = await tx.wait()
    if (!receipt.status) {
      const error = await getTransactionError(tx.hash)
      throw new Error(`Transaction failed: ${error}`)
    }

    const multiSigContract = _getContract({
      daoAddress: window.Globals.selectedDao.multiSigAddress,
      abiMethods: ABIS.MULTI_SIG.methods,
    })

    return tx
  } catch (error) {
    console.error('❌ Error queueing proposal:', {
      error: error.message,
      errorCode: error.code,
      errorData: error.data,
      errorReason: error.reason,
      proposalState: await getState(proposalId).catch(() => 'Unknown'),
      targets,
      values,
      calldatas: calldatas.map((cd) => cd.substring(0, 42) + '...'),
      descriptionHash,
      txHash: error.transactionHash,
    })
    throw error
  }
}

/**
 * @param {Proposal['id']} proposalId
 */
export async function confirmTransaction(proposalId) {
  try {
    const contract = _getContract({
      daoAddress: window.Globals.selectedDao.multiSigAddress,
      abiMethods: ABIS.MULTI_SIG.methods,
    })

    const gasLimit = 3000000

    // TODO: W3 READ - Uses Infura token via window.ethereum provider
    const signer = new ethers.providers.Web3Provider(
      window.ethereum
    ).getSigner()
    const signerAddress = await signer.getAddress()
    const balance = await signer.getBalance()

    const txIndex = await findTransactionIndexByProposalId(proposalId)

    if (txIndex === undefined || txIndex === null || txIndex < 0) {
      throw new Error(`Invalid transaction index: ${txIndex}`)
    }

    // TODO: W3 READ - Uses Infura token via contract.getTransactionCount call
    const totalTxCount = await contract.getTransactionCount()

    if (txIndex >= totalTxCount) {
      throw new Error(
        `Transaction index ${txIndex} is out of bounds. Total transactions: ${totalTxCount}`
      )
    }

    let txData
    try {
      // TODO: W3 READ - Uses Infura token via contract.getTransaction call
      txData = await contract.getTransaction(txIndex)
    } catch (txDataError) {
      throw new Error(`Failed to get transaction data: ${txDataError.message}`)
    }

    if (!txData) {
      throw new Error('Transaction data not found')
    }

    // TODO: W3 READ - Uses Infura token via contract.isConfirmed call
    const isConfirmed = await contract.isConfirmed(txIndex, signerAddress)

    if (isConfirmed) {
      alert('Transaction has already been confirmed by this user')
      return
    }

    // TODO: W3 READ - Uses Infura token via contract.numConfirmationsRequired call
    const requiredConfirmations = await contract.numConfirmationsRequired()
    const currentConfirmations = txData.numConfirmations
    const isExecuted = txData.executed

    if (isExecuted) {
      throw new Error('Transaction is already executed. Cannot confirm.')
    }

    const isTimelockTransaction =
      txData.to &&
      txData.to.toLowerCase() ===
        window.Globals.selectedDao.timeLockAddress.toLowerCase()

    try {
      const timelockReady = await getProposalTimelockReady(
        window.Globals.selectedDao.timeLockAddress,
        window.Globals.selectedDao.governorAddress,
        window.Globals.selectedDao.registryAddress,
        proposalId,
        window.Globals.selectedAccount.walletAddress
      )

      if (
        timelockReady &&
        typeof timelockReady === 'object' &&
        'isReady' in timelockReady
      ) {
        if (timelockReady.isReady === false) {
          const readyTime = new Date(timelockReady.readyTimestamp * 1000)
          const currentTime = new Date(timelockReady.currentTime * 1000)

          const timeUntilReady =
            timelockReady.readyTimestamp - timelockReady.currentTime
          const hoursUntilReady = Math.ceil(timeUntilReady / 3600)

          throw new Error(
            'Timelock operation is not ready yet. Ready at: ' +
              readyTime.toISOString() +
              ' (' +
              hoursUntilReady +
              ' hours from now). Please wait until the timelock period has elapsed before confirming this transaction.'
          )
        }
      } else if (timelockReady === '0') {
        // Timelock readiness returned 0 - proposal may not be found
      } else if (
        timelockReady &&
        typeof timelockReady === 'object' &&
        'status' in timelockReady
      ) {
        // Timelock readiness check returned error status
      }
    } catch (timelockError) {
      // Error checking timelock readiness
    }

    try {
      const gasEstimate = await contract.estimateGas.confirmTransaction(txIndex)
    } catch (gasError) {
      // Gas estimation failed
    }

    const tx = await contract.confirmTransaction(txIndex, { gasLimit })

    const receipt = await tx.wait()

    if (!receipt.status) {
      const error = await getTransactionError(tx.hash)
      throw new Error(`Transaction failed: ${error}`)
    }

    // TODO: W3 READ - Uses Infura token via contract.getTransaction call
    const updatedTxData = await contract.getTransaction(txIndex)
    const updatedConfirmations = updatedTxData.numConfirmations
    // TODO: W3 READ - Uses Infura token via contract.isConfirmed call
    const userConfirmed = await contract.isConfirmed(txIndex, signerAddress)

    return tx
  } catch (error) {
    throw error
  }
}

export async function getConfirmationsRequired() {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.multiSigAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })
  // TODO: W3 READ - Uses Infura token via contract.numConfirmationsRequired call
  return (await contract.numConfirmationsRequired()).toNumber()
}

export async function getConfirmations() {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.multiSigAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })
  // TODO: W3 READ - Uses Infura token via contract.numConfirmations call
  return (await contract.numConfirmations()).toNumber()
}

/**
 * @param {Proposal['id']} proposalId
 */
export async function executeTransaction(proposalId) {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.multiSigAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })
  const gasLimit = 3000000
  let txIndex
  try {
    txIndex = await findTransactionIndexByProposalId(proposalId)

    // TODO: W3 READ - Uses Infura token via contract.isExecutable call
    const isExecutable = await contract.isExecutable(txIndex)
    if (!isExecutable) {
      throw new Error('Transaction is not executable. Aborting.')
    }

    // TODO: W3 READ - Uses Infura token via contract.getTransaction call
    const txData = await contract.getTransaction(txIndex)
    const numConfirmations = txData.numConfirmations
    // TODO: W3 READ - Uses Infura token via contract.numConfirmationsRequired call
    const required = await contract.numConfirmationsRequired()
    const executed = txData.executed
    if (executed) {
      throw new Error('Transaction already executed. Aborting.')
    }
    let notEnoughConfirmations = false
    if (
      typeof numConfirmations.lt === 'function' &&
      typeof required.toNumber === 'function'
    ) {
      notEnoughConfirmations = numConfirmations.lt(required)
    } else {
      notEnoughConfirmations = Number(numConfirmations) < Number(required)
    }
    if (notEnoughConfirmations) {
      throw new Error('Not enough confirmations. Aborting.')
    }

    try {
      // TODO: W3 READ - Uses Infura token via contract.callStatic.executeTransaction call
      await contract.callStatic.executeTransaction(txIndex, { gasLimit })
    } catch (err) {
      throw new Error(
        'Pre-flight simulation failed: ' + (err.reason || err.message)
      )
    }

    const tx = await contract.executeTransaction(txIndex, { gasLimit })
    if (tx && tx.wait) {
      const receipt = await tx.wait()
      if (receipt && receipt.status === 0) {
        try {
          const revertReason = await getTransactionError(tx.hash)
        } catch (revertErr) {
          // Error getting revert reason
        }
      }
    }
    return tx
  } catch (error) {
    try {
      if (error.transactionHash) {
        const revertReason = await getTransactionError(error.transactionHash)
      }
    } catch (revertErr) {
      // Error getting revert reason in catch
    }
    throw error
  }
}

/**
 * @param {Proposal['id']} proposalId
 */
export async function checkIfExecutable(proposalId) {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.multiSigAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })
  try {
    const txIndex = await findTransactionIndexByProposalId(proposalId)
    // TODO: W3 READ - Uses Infura token via contract.isExecutable call
    return contract.isExecutable(txIndex)
  } catch (error) {
    return false
  }
}

/**
 * @param {Proposal['id']} proposalId
 */
export async function checkIfUserHasConfirmedTransaction(proposalId) {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.multiSigAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })
  try {
    const txIndex = await findTransactionIndexByProposalId(proposalId)
    // TODO: W3 READ - Uses Infura token via contract.isConfirmed call
    return contract.isConfirmed(
      txIndex,
      window.Globals.selectedAccount.walletAddress
    )
  } catch (error) {
    return false
  }
}

export async function getNumConfirmations(proposalId) {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.multiSigAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })

  // TODO: W3 READ - Uses Infura token via contract.getTransactionCount call
  const count = await contract.getTransactionCount()
  // TODO: W3 READ - Uses Infura token via contract.getTransactions call
  const transactions = await contract.getTransactions(0, count)
  const txIndex = await findTransactionIndexByProposalId(proposalId)
  const numConfirmations = transactions[txIndex].numConfirmations.toNumber()
  return numConfirmations
}

export async function getTransactionCount() {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.multiSigAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })

  // TODO: W3 READ - Uses Infura token via contract.getTransactionCount call
  const txCount = await contract.getTransactionCount()
  return txCount
}

/**
 * @param {string} txHash
 */
async function getTransactionError(txHash) {
  // TODO: W3 READ - Uses Infura token via window.ethereum provider
  const provider = new ethers.providers.Web3Provider(window.ethereum)
  let receipt
  let tx
  try {
    // TODO: W3 READ - Uses Infura token via provider.getTransaction call
    tx = await provider.getTransaction(txHash)
  } catch (err) {
    throw err
  }

  try {
    // TODO: W3 READ - Uses Infura token via provider.waitForTransaction call
    receipt = await provider.waitForTransaction(txHash)
  } catch (err) {
    throw err
  }

  try {
    // TODO: W3 READ - Uses Infura token via provider.call call
    await provider.call(
      {
        from: tx.from,
        to: tx.to,
        data: tx.data,
        value: tx.value,
      },
      receipt.blockNumber
    )
  } catch (err) {
    const reason = err.data?.message || err.message
    return reason
  }
}

export async function findTransactionIndexByProposalId(target) {
  const contract = _getContract({
    daoAddress: window.Globals.selectedDao.multiSigAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })
  // TODO: W3 READ - Uses Infura token via contract.getTransactionCount call
  const count = await contract.getTransactionCount()
  // TODO: W3 READ - Uses Infura token via contract.getTransactions call
  const transactions = await contract.getTransactions(0, count)
  const proposalIds = []

  transactions.forEach((tx, index) => {
    try {
      if (tx.proposalId) {
        const proposalIdStr = tx.proposalId.toString()
        proposalIds.push(proposalIdStr)
      }
    } catch (err) {
      // Error processing transaction
    }
  })

  const index = proposalIds.findIndex((id) => id === target.toString())

  if (index === -1) {
    throw new Error(`Transaction not found for proposal ID: ${target}`)
  }

  return index
}

/**
 * @param {string} tokenAddress - The address of the token contract
 * @param {string} delegateeAddress - The address to delegate voting power to
 */
export async function delegateVotingPower(tokenAddress, delegateeAddress) {
  // TODO: W3 READ - Uses Infura token via window.ethereum provider
  const provider = new ethers.providers.Web3Provider(window.ethereum)
  const signer = provider.getSigner()
  const gasLimit = 3000000

  const tokenContract = new ethers.Contract(
    tokenAddress,
    ABIS.REGIONAL_DAO_TOKEN.methods,
    signer
  )

  try {
    // TODO: W3 READ - Uses Infura token via contract.balanceOf call
    const balance = await tokenContract.balanceOf(await signer.getAddress())
    // TODO: W3 READ - Uses Infura token via contract.delegates call
    const currentDelegate = await tokenContract.delegates(
      await signer.getAddress()
    )

    const tx = await tokenContract.delegate(delegateeAddress, {
      gasLimit,
    })

    const receipt = await tx.wait()
    // TODO: W3 READ - Uses Infura token via contract.delegates call
    const newDelegate = await tokenContract.delegates(await signer.getAddress())
    // TODO: W3 READ - Uses Infura token via contract.getVotes call
    const votingPower = await tokenContract.getVotes(await signer.getAddress())

    return tx
  } catch (error) {
    throw error
  }
}

/**
 * @param {string} tokenAddress - The address of the token contract
 * @param {string} accountAddress - The address to check voting power for
 * @returns {Promise<{delegatedTo: string, votingPower: string}>}
 */
export async function getVotingPowerInfo(tokenAddress, accountAddress) {
  // TODO: W3 READ - Uses Infura token via window.ethereum provider
  const provider = new ethers.providers.Web3Provider(window.ethereum)
  const signer = provider.getSigner()

  const tokenContract = new ethers.Contract(
    tokenAddress,
    ABIS.REGIONAL_DAO_TOKEN.methods,
    signer
  )

  try {
    // TODO: W3 READ - Uses Infura token via contract.delegates call
    const delegatedTo = await tokenContract.delegates(accountAddress)
    // TODO: W3 READ - Uses Infura token via contract.getVotes call
    const votingPower = await tokenContract.getVotes(accountAddress)

    return {
      delegatedTo,
      votingPower: votingPower.toString(),
    }
  } catch (error) {
    throw error
  }
}

export async function getProposalTimelockReady(
  timeLockAddress,
  daoAddress,
  registryAddress,
  proposalId,
  creatorAddress
) {
  const contract = _getContract({
    daoAddress: timeLockAddress,
    abiMethods: ABIS.TIMELOCK.methods,
  })

  try {
    const governorContract = _getContract({
      daoAddress: daoAddress,
      abiMethods: ABIS.REGIONAL_DAO.methods,
    })

    const proposals = await kulaApiFetch(
      `/v1/contracts/daos/proposals?daoaddress=${window.Globals.selectedDao.address}`,
      {
        method: 'GET',
      }
    ).then((response) => response.json())

    const proposal = proposals.find(
      (p) => p.id.toString() === proposalId.toString()
    )

    if (!proposal) {
      return '0'
    }

    const descriptionHash = ethers.utils.keccak256(
      ethers.utils.toUtf8Bytes(proposal.description || '')
    )

    let registryAddress
    try {
      registryAddress =
        proposal.registryAddress || window.Globals.selectedDao.registryAddress
    } catch (error) {
      registryAddress = window.Globals.selectedDao.registryAddress
    }

    const proposerWalletAddress = creatorAddress

    if (!registryAddress || !proposerWalletAddress) {
      return {
        status: 'error',
        message: 'Missing required proposal parameters',
        proposalId: proposalId,
      }
    }

    // TODO: W3 READ - Uses Infura token via window.ethereum provider
    const registryContract = new ethers.Contract(
      registryAddress,
      ABIS.REGISTRY.methods,
      new ethers.providers.Web3Provider(window.ethereum).getSigner()
    )

    const encodedFunction = registryContract.interface.encodeFunctionData(
      'storeDaoAction',
      [daoAddress, creatorAddress, proposal.description]
    )

    const calldatas = [encodedFunction]
    const values = [0]
    const targets = [registryAddress]
    const predecessor = ethers.constants.HashZero

    // TODO: W3 READ - Uses Infura token via contract.hashOperationBatch call
    const operationId = await contract.hashOperationBatch(
      targets,
      values,
      calldatas,
      predecessor,
      descriptionHash
    )

    // TODO: W3 READ - Uses Infura token via contract.getTimestamp call
    const readyTimestamp = await contract.getTimestamp(operationId)
    const currentTime = Math.floor(Date.now() / 1000)
    const isReady = readyTimestamp > 1 && readyTimestamp <= currentTime

    const returnObject = {
      readyTimestamp: readyTimestamp.toString(),
      currentTime: currentTime,
      isReady: isReady,
    }

    return returnObject
  } catch (error) {
    return {
      status: 'error',
      error: error.message,
      proposalId: proposalId,
    }
  }
}

/**
 * Get original proposal parameters from ProposalCreated event
 * @param {string} daoAddress
 * @param {string} proposalId
 * @returns {Promise<{targets: string[], values: string[], calldatas: string[], descriptionHash: string} | null>}
 */
async function getOriginalProposalParameters(daoAddress, proposalId) {
  try {
    // TODO: W3 READ - Uses Infura token via window.ethereum provider
    const provider = new ethers.providers.Web3Provider(window.ethereum)

    // Import the full ABI to access events
    const RegionalDaoGovernorAbi = await import(
      '../../../contracts/RegionalDaoGovernor.abi.js'
    )
    const contract = new ethers.Contract(
      daoAddress,
      RegionalDaoGovernorAbi.default,
      provider
    )

    // Get ProposalCreated events - we can't filter by proposalId since it's not indexed
    const filter = contract.filters.ProposalCreated()
    // TODO: W3 READ - Uses Infura token via contract.queryFilter call
    const events = await contract.queryFilter(filter, 0, 'latest')

    // Find the event for our proposal ID
    const targetEvent = events.find((event) => {
      const eventProposalId = event.args?.proposalId?.toString()
      return eventProposalId === proposalId.toString()
    })

    if (!targetEvent) {
      console.error(
        '❌ ProposalCreated event not found for proposal ID:',
        proposalId
      )
      return null
    }

    const eventArgs = targetEvent.args

    // Extract parameters - use indexed access to avoid conflicts with Result object methods
    const targets = eventArgs[2]
    const values = eventArgs[3]
    const calldatas = eventArgs[5]
    const description = eventArgs[8]

    const descriptionHash = ethers.utils.keccak256(
      ethers.utils.toUtf8Bytes(description)
    )

    return {
      targets: targets,
      values: values,
      calldatas: calldatas,
      descriptionHash: descriptionHash,
    }
  } catch (error) {
    console.error('❌ Error getting original proposal parameters:', error)
    return null
  }
}

/**
 * Queue transaction using original parameters
 * @param {ethers.Contract} governorContract
 * @param {{targets: string[], values: string[], calldatas: string[], descriptionHash: string}} originalParams
 * @param {string} proposalId
 */
async function queueWithOriginalParameters(
  governorContract,
  originalParams,
  proposalId
) {
  try {
    const tx = await governorContract.queue(
      originalParams.targets,
      originalParams.values,
      originalParams.calldatas,
      originalParams.descriptionHash,
      {
        gasLimit: 5000000,
      }
    )

    const receipt = await tx.wait()
    if (!receipt.status) {
      const error = await getTransactionError(tx.hash)
      throw new Error(`Transaction failed: ${error}`)
    }

    return tx
  } catch (error) {
    console.error('❌ Error queueing with original parameters:', error)
    throw error
  }
}

export async function setProposalVisibility(proposalId, hidden) {
  return await kulaApiFetch(
    `/v1/contracts/daos/proposals/visibility?proposalid=${proposalId}&hidden=${hidden}`,
    {
      method: 'PATCH',
    }
  )
}
